import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";

import PropTypes from "prop-types";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import PaddedCell from "@cayuse/shared-react.components.padded-cell";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import AssignSelected from "components/AssignSelected";
import DownloadCSVModal from "components/DownloadCSVModal";
import FacetedSearchBar from "components/FacetedSearchBar";
import FeedTable from "components/FeedTable";
import SetView from "components/SetView";
import SPLink from "components/SPLink";
import {
	AWARDS_BASE_ROUTE,
	PRIVILEGES, //TODO: CHANGE THE ROLES AND PRIVILEGES
	ROLE_SP_AWARD_ADMINISTRATOR,
	COLUMN_HEADER_KEYS,
	MODIFICATION_DRAFT,
} from "globals/constants";
import {
	useDictionary,
	usePagination,
	useSelectedRows,
	useHasPrivileges,
	useHasRoles,
	useDownloadCsv,
	useColumnHeaders,
	useRequestChain,
	useActiveColumns
} from 'hooks';
import {
	findAwards,
	setPaging,
	updateAdminTeamMembersPerAward,
} from "store/actions/awardList";
import {
	exportReportAsCSV,
	notifyError,
	getKeysFromColumnHeaders,
	generateDatedDownloadFileName,
} from "utils";
import { awardListSelectors, shellSelectors } from "store/selectors";

import styles from "./styles.css";
import { isEqual } from 'lodash';

const AwardListView = (
	{ awardsListId,
		searchFacets,
		onSearchChange,
		availableColumns,
		actualFacets
	}) => {
	const { pageLimit, pageOffset, setPageLimit, setPageNumber } = usePagination("awards", setPaging);
	const [ isAssignmentOpen, setIsAssignmentOpen ] = useState(false);
	const [ isSetViewOpen, setIsSetViewOpen ] = useState(false);
	const [ isDownloadOpen, setIsDownloadOpen ] = useState(false);
	const [ isAssignToMeOnly, setIsAssignToMeOnly ] = useState(false);
	const [ rows, setRows ] = useState([]);
	const [ previousSearch, setPreviousSearch ] = useState({});
	const getRowKey = useMemo(() => row => row._item.id, []);
	const { selectedRows, isSelectAll, setRowSelection, clearSelections } = useSelectedRows({ rows, getRowKey });

	const facetFilterRequestChain = useRequestChain();

	const dictionary = useDictionary("awards");

	const { isDownloading, download } = useDownloadCsv();

	const {
		[PRIVILEGES.AWARD_ADMIN_TEAM_EDIT]: canAssign
	} = useHasPrivileges([
		PRIVILEGES.AWARD_ADMIN_TEAM_EDIT
	]);

	const {
		[ROLE_SP_AWARD_ADMINISTRATOR]: isAwardAdmin
	} = useHasRoles([
		ROLE_SP_AWARD_ADMINISTRATOR
	]);

	const {
		items,
		totalItems,
		search,
		isFetching: isLoading,
		isRefresh,
	} = useSelector(awardListSelectors.content);
	const user = useSelector(shellSelectors.user);
	const { columnHeaders: formColumnHeaders } = useColumnHeaders(COLUMN_HEADER_KEYS.AWARD);
	const { columns: activeColumns, updateActiveColumns } = useActiveColumns("AWARD", formColumnHeaders);

	const updatedActiveColumns = useMemo(
		() => activeColumns.map(
			activeColumn => formColumnHeaders.find(formColumnHeader => activeColumn.id === formColumnHeader.id)
		),
		[ activeColumns, formColumnHeaders ],
	);

	const requiredFieldKeys = useMemo(() => getKeysFromColumnHeaders(activeColumns), [ activeColumns ]);

	const onAssignedToMeClicked = () => {
		setIsAssignToMeOnly(!isAssignToMeOnly);
		setPageNumber(1);
	};

	const showAssignedToMeButton = isAwardAdmin
		? (
			<label className={styles.assignedToMeOnly}>
				{dictionary.assignedToMeOnly || "See assigned to me only"}
				<input
					name="assignButton"
					id="assignButton"
					type="checkbox"
					aria-label={dictionary.assignedToMeOnly || "See assigned to me only"}
					onClick={onAssignedToMeClicked}
				/>
			</label>
		) : "";

	const handleTeamAssignment = idTeamArray => {
		clearSelections();
		updateAdminTeamMembersPerAward(idTeamArray);
	};

	const assignSelectedContent = canAssign
		? (
			<AssignSelected
				editMembers={false}
				instanceFeedItemSelections={selectedRows}
				isOpen={isAssignmentOpen}
				onTeamModification={handleTeamAssignment}
				onOpen={() => setIsAssignmentOpen(true)}
				onClose={() => setIsAssignmentOpen(false)}
				adminRole={ROLE_SP_AWARD_ADMINISTRATOR}
				formType={COLUMN_HEADER_KEYS.AWARD}
				isFeed
			/>
		)
		: "";

	const downloadCSV = async ({ selections: selectedColumnHeaders }) => {
		const filename = generateDatedDownloadFileName({
			prefix: dictionary.filePrefix || "SP_Awards",
			extension: "csv"
		});

		await exportReportAsCSV({
			submissionType: "award",
			selectedColumnHeaders,
			filename,
			download,
			onComplete: () => setIsDownloadOpen(false),
			onError: notifyError
		});
	};

	const hasItems = isNonEmptyArray(items);

	useEffect(() => {
		const updatedRows = hasItems
			? items.map(item => {
				const isNumberLinkable = isNonEmptyString(item.awardId) && isNonEmptyString(item.awardNumber);
				const linkedAwardNumber = isNumberLinkable
					? <SPLink to={`${AWARDS_BASE_ROUTE}/${item.awardId}`}>{item.awardNumber}</SPLink>
					: "";

				item.subRows.forEach(subRow => {
					if (subRow.modificationStatus === MODIFICATION_DRAFT) {
						subRow.awardNumber = <span className={styles.draftAwardNumber}>{subRow.awardNumber} (Draft)</span>;
					}
				});

				return {
					_isSelected: selectedRows.has(item.id),
					_item: item,
					...item,
					awardNumber: linkedAwardNumber
				};
			})
			: [];
		setRows(updatedRows);
	}, [ items, selectedRows, hasItems ]);

	const combinedSearchTerms = useMemo(() => {
		const assignToMeTerm = isAssignToMeOnly
			? { "adminTeamMembers.personId": user?.personId || "" }
			: {};

		return { ...search, ...assignToMeTerm };
	}, [ search, isAssignToMeOnly, user ]);

	useEffect(() => {
		if (!isEqual(search, previousSearch)) {
			setPreviousSearch(search);
			setPageNumber(1);
		}
	}, [ previousSearch, search ]);

	useEffect(() => {
		if (activeColumns?.length > 0 && (!isEqual(previousSearch, search) || !isLoading)) {
			facetFilterRequestChain.addRequest(
				'refreshAwards',
				() => {
					updateActiveColumns(updatedActiveColumns, false);

					return findAwards({
						pageLimit,
						pageOffset,
						searchTerms: combinedSearchTerms,
						fieldKeys: requiredFieldKeys
					});
				});
		}
	}, [
		combinedSearchTerms,
		pageLimit,
		pageOffset,
		requiredFieldKeys,
		updatedActiveColumns
	]);

	return (
		<div id="list-award">
			<VerticalSpacing>
				<BasePadding>
					<PaddedCell className={styles.proposalListOptions}
						radius={{ top: true }}
						border={{ top: true, left: true, right: true }}
					>
						<div className={styles.proposalListSearch}>
							<FacetedSearchBar
								ariaLabel={dictionary.awardsSearchAriaLabel || "Search awards"}
								facetAriaControls={awardsListId}
								facets={searchFacets}
								onChange={onSearchChange}
								actualFacets={actualFacets}
								type="AWARD"
							/>
						</div>
						<div className={styles.proposalListActions}>
							{showAssignedToMeButton}

							{assignSelectedContent}

							<SetView
								isOpen={isSetViewOpen}
								options={availableColumns}
								selections={activeColumns}
								onOpen={() => setIsSetViewOpen(true)}
								onClose={() => setIsSetViewOpen(false)}
								onUpdate={updateActiveColumns}
								requireKeys={false}
							/>
							<DownloadCSVModal
								isOpen={isDownloadOpen}
								onClick={() => setIsDownloadOpen(!isDownloadOpen)}
								onCancel={() => setIsDownloadOpen(false)}
								onSave={downloadCSV}
								options={availableColumns}
								selections={activeColumns}
								isDownloading={isDownloading}
							/>
						</div>
					</PaddedCell>

					<div>
						<FeedTable
							isLoading={isLoading}
							tableid={awardsListId}
							isRefresh={isRefresh}
							headers={activeColumns}
							rows={rows}
							totalItems={totalItems}
							pageLimit={pageLimit}
							pageOffset={pageOffset}
							onPageChange={setPageNumber}
							onPageLimitChange={setPageLimit}
							onRowSelectionChange={({ row, isSelected }) => setRowSelection(row, isSelected)}
							overflow="visible"
							isSelectable={canAssign}
							isExpandable={true}
							isSelectAll={isSelectAll}
							accessibilityLabel={({ projectTitle, _item }) => (
								`${projectTitle}, ${dictionary.accessibilityLabelAwardNumber || "Award #"}${_item.awardNumber}`
							)}
							noResultsLabel={dictionary.noResults || "No Saved Awards"}
							captionText="Awards"
						/>
					</div>
				</BasePadding>
			</VerticalSpacing>
		</div>
	);
};

AwardListView.propTypes = {
	awardsListId: PropTypes.string
};

export default AwardListView;
